import { useTraderTime } from '../contexts/TraderTimeContext';
import { formatTraderMessage } from '../lib/traderTime';
import { formatCardNumber } from '../lib/utils';
import { WithdrawReceiptDownload } from '../components/WithdrawReceiptDownload';
import { bankDisplayName } from '../lib/kgsBanks';
import { usePaymentDeadline, usesLocalDeadline } from '../hooks/usePaymentDeadline';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { paymentsAPI, PaymentTransactionResponse, traderAPI, WithdrawLimitStatus, WithdrawReceipt } from '../lib/api';
import { formatAmount, formatAmountWithSeparator, getExchangeRate } from '../lib/utils';
import { balanceEvents, BALANCE_EVENTS } from '../lib/events';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { Loader2, ExternalLink, CheckCircle, Clock, Upload, RefreshCw, Copy, ChevronDown } from 'lucide-react';
import { CountdownTimer } from '../components/ui/countdown-timer';

const CopyRequisite: React.FC<{ value: string; label: string; phone?: boolean; children: React.ReactNode }> = ({ value, label, phone = false, children }) => {
    const [notice, setNotice] = useState('');
    return <div className="min-w-0 text-right">
        <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="select-all break-all">{children}</span>
            <Button type="button" variant="ghost" size="sm" aria-label={`Скопировать ${label}`}
                onClick={async () => {
                    try {
                        const digits = value.replace(/[^0-9]/g, '');
                        await navigator.clipboard.writeText((phone && value.trim().startsWith('+') ? '+' : '') + digits);
                        setNotice('Скопировано');
                    } catch {
                        setNotice('Не удалось скопировать. Выделите номер и скопируйте вручную.');
                    }
                }}>
                <Copy className="h-4 w-4" />
            </Button>
        </div>
        {notice && <span role="status" className="text-xs font-normal">{notice}</span>}
    </div>;
};

const TimedConfirmButton: React.FC<{
    transaction: PaymentTransactionResponse; busy: boolean; onClick: () => void; children: React.ReactNode;
    className?: string; size?: 'sm'; blocked?: boolean;
}> = ({ transaction, busy, onClick, children, className, size, blocked }) => {
    const { expired } = usePaymentDeadline({
        expiresAt: transaction.expires_at, assignedAt: transaction.assigned_at,
        durationSeconds: transaction.duration_seconds, serverTime: transaction.server_time,
    });
    return <Button onClick={onClick} className={className} size={size}
        disabled={busy || blocked || (usesLocalDeadline(transaction.timing_policy) && expired)}>{children}</Button>;
};

const getRequisiteTypeLabel = (type?: string | null) => {
    switch (type) {
        case 'sbp': return 'СБП';
        case 'sbp_kgs': return 'СБП KGS';
        case 'card': return 'Банковская карта';
        case 'sbp_qr': return 'СБП QR';
        case 'mobile_topup': return 'Мобильное пополнение';
        case 'mobile_kgs': return 'Мобильный платёж';
        case 'mobile_tjs': return 'Mobile Commerce TJS';
        case 'qr_vietnam_sber': return 'QR Vietnam — Sber';
        case 'qr_vietnam_vtb': return 'QR Vietnam — VTB';
        case 'psb_ecom': return 'PSB E-com';
        case 'transgran_card_rub_arm': return 'transgran_card_rub_arm';
        case 'transgran_mobile_rub_arm': return 'transgran_mobile_rub_arm';
        case 'elqr': return 'ElQR';
        default: return type || 'Не указан';
    }
};

type PaymentDirection = 'deposit' | 'withdraw';

// A new direction owns fresh pagination, requests and receipt state.
export const ActivePaymentsPage: React.FC<{ direction: PaymentDirection }> = ({ direction }) => (
    <ActivePaymentsList key={direction} direction={direction} />
);

const ActivePaymentsList: React.FC<{ direction: PaymentDirection }> = ({ direction }) => {
    const { formatDate, offsetMinutes } = useTraderTime();
    const [transactions, setTransactions] = useState<PaymentTransactionResponse[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(50);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [confirmationNotice, setConfirmationNotice] = useState<string | null>(null);
    const [confirmingTransaction, setConfirmingTransaction] = useState<string | null>(null);
    const [withdrawLimit, setWithdrawLimit] = useState<WithdrawLimitStatus | null>(null);
    const [receiptFiles, setReceiptFiles] = useState<Record<string, { id: string; file: File }[]>>({});
    const [savedReceipts, setSavedReceipts] = useState<Record<string, WithdrawReceipt[]>>({});
    const [uploadProgress, setUploadProgress] = useState<{ transactionId: string; done: number; total: number } | null>(null);
    const confirmationInFlight = useRef(false);
    const getSavedReceipts = (transaction: PaymentTransactionResponse): WithdrawReceipt[] => {
        const items = [...(transaction.withdraw_receipts || []), ...(savedReceipts[transaction.id] || [])];
        if (items.length) return Array.from(new Map(items.map(item => [item.id, item])).values());
        return transaction.withdraw_receipt_filename ? [{ id: '', filename: transaction.withdraw_receipt_filename }] : [];
    };
    const getPendingReceipts = (transaction: PaymentTransactionResponse) => {
        const saved = new Set(getSavedReceipts(transaction).map(item => item.id));
        return (receiptFiles[transaction.id] || []).filter(item => !saved.has(item.id));
    };
    const isLoadingRef = useRef(false);
    const loadRequestRef = useRef(0);
    const assignmentVersions = useRef<Record<string, number>>({});

    const loadWithdrawLimit = useCallback(async () => {
        if (direction !== 'withdraw') return;
        try {
            setWithdrawLimit(await traderAPI.getWithdrawLimitStatus());
        } catch (err) {
            console.error('Ошибка загрузки лимита выплат:', err);
        }
    }, [direction]);

    useEffect(() => {
        if (direction !== 'withdraw') return;
        loadWithdrawLimit();
        const interval = setInterval(loadWithdrawLimit, 5000);
        return () => clearInterval(interval);
    }, [direction, loadWithdrawLimit]);

    const loadActivePayments = useCallback(async (showLoading = true, force = false) => {
        if (isLoadingRef.current && !force) {
            return;
        }
        const requestId = ++loadRequestRef.current;
        try {
            isLoadingRef.current = true;
            if (showLoading) {
                setIsLoading(true);
            }
            setError(null);
            const data = await paymentsAPI.getTraderActivePayments(currentPage, pageSize, direction);
            if (requestId !== loadRequestRef.current) return;

            const lastPage = Math.max(1, data.pages);
            if (currentPage > lastPage) {
                setCurrentPage(lastPage);
                return;
            }

            const changed = data.transactions.filter(item =>
                assignmentVersions.current[item.id] !== undefined &&
                assignmentVersions.current[item.id] !== (item.assignment_version || 0)).map(item => item.id);
            if (changed.length) {
                setReceiptFiles(previous => { const next = { ...previous }; changed.forEach(id => delete next[id]); return next; });
                setSavedReceipts(previous => { const next = { ...previous }; changed.forEach(id => delete next[id]); return next; });
            }
            data.transactions.forEach(item => { assignmentVersions.current[item.id] = item.assignment_version || 0; });

            // Обновляем только если данные действительно изменились
            setTransactions(prevTransactions => {
                const dataString = JSON.stringify(data.transactions);
                const prevString = JSON.stringify(prevTransactions);

                // Если данные не изменились, не обновляем состояние
                if (dataString === prevString) {
                    return prevTransactions;
                }

                return data.transactions;
            });

            setTotalPages(data.pages);
            setTotalItems(data.total);
        } catch (err: any) {
            if (requestId !== loadRequestRef.current) return;
            console.error('Ошибка загрузки активных платежей:', err);
            setError('Ошибка при загрузке активных платежей');
        } finally {
            if (requestId === loadRequestRef.current) {
                isLoadingRef.current = false;
                setIsLoading(false);
            }
        }
    }, [currentPage, pageSize, direction]);

    useEffect(() => {
        loadActivePayments(true, true);
        return () => { ++loadRequestRef.current; };
    }, [loadActivePayments]);
    useEffect(() => {
        const interval = setInterval(() => {
            console.log('Refreshing active payments...');
            loadActivePayments(false);
        }, 5000);

        return () => clearInterval(interval);
    }, [loadActivePayments]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleConfirmPayment = async (transactionId: string) => {
        if (confirmationInFlight.current) return;
        confirmationInFlight.current = true;
        try {
            setConfirmingTransaction(transactionId);
            setConfirmationNotice(null);
            setError(null);
            const transaction = transactions.find(item => item.id === transactionId);
            if (transaction?.order_info?.order_type === 'withdraw') {
                const pending = getPendingReceipts(transaction);
                const count = getSavedReceipts(transaction).length + pending.length;
                if (!count) throw new Error('Перед подтверждением выплаты прикрепите чек');
                if (count > 10) throw new Error('К выплате можно прикрепить не более 10 чеков');
                setUploadProgress({ transactionId, done: 0, total: pending.length });
                for (let index = 0; index < pending.length; index++) {
                    const item = pending[index];
                    const saved = await paymentsAPI.uploadWithdrawReceipt(transactionId, item.file, transaction.assignment_version || 0, item.id);
                    if (assignmentVersions.current[transactionId] !== (transaction.assignment_version || 0)) {
                        throw new Error('Исполнитель выплаты изменился. Обновите список платежей.');
                    }
                    setSavedReceipts(previous => ({ ...previous, [transactionId]: saved.withdraw_receipts }));
                    setReceiptFiles(previous => ({ ...previous, [transactionId]: (previous[transactionId] || []).filter(file => file.id !== item.id) }));
                    setUploadProgress({ transactionId, done: index + 1, total: pending.length });
                }
            }
            const result = await paymentsAPI.confirmPayment(transactionId, undefined, transaction?.assignment_version || 0);
            if (result.already_confirmed) setConfirmationNotice('Платёж уже подтверждён');
            // Обновляем список после подтверждения
            await loadActivePayments(false, true);
            // Уведомляем sidebar об обновлении баланса
            balanceEvents.emit(BALANCE_EVENTS.PAYMENT_CONFIRMED);
        } catch (err: any) {
            await loadActivePayments(false, true);
            setError(err.response?.data?.detail || err.message || 'Ошибка при подтверждении платежа');
        } finally {
            confirmationInFlight.current = false;
            setConfirmingTransaction(null);
            setUploadProgress(null);
            await loadWithdrawLimit();
        }
    };

    const getStatusBadge = (transaction: PaymentTransactionResponse) => {
        switch (transaction.status) {
            case 'assigned':
                return <Badge variant="secondary">Назначен</Badge>;
            case 'pending_requisites':
                return <Badge variant="outline" className="text-yellow-700 dark:text-yellow-300 border-yellow-400 bg-yellow-50 dark:bg-yellow-950">Ожидает реквизитов</Badge>;
            case 'no_traders':
                return <Badge variant="outline" className="text-gray-500 dark:text-slate-400 border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">404</Badge>;
            case 'receipt_uploaded':
                return transaction.receipt_filename
                    ? <Badge variant="default">Чек загружен</Badge>
                    : <Badge variant="default">Перевод подтверждён</Badge>;
            case 'confirmed':
                return <Badge className="bg-green-600">Подтвержден</Badge>;
            case 'completed':
                return <Badge className="bg-green-800">Завершен</Badge>;
            default:
                return <Badge variant="outline">{transaction.status}</Badge>;
        }
    };


    const renderPayment = (transaction: PaymentTransactionResponse) => (
                        <Card key={transaction.id} className="border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow">
                            <details className="group">
                            <summary className="list-none cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 [&::-webkit-details-marker]:hidden">
                            <CardHeader className="p-3 sm:p-4 space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                    <CardTitle className="text-sm sm:text-base break-all leading-snug">
                                        Платеж #{transaction.id}
                                    </CardTitle>
                                    <div className="flex shrink-0 items-center gap-2">
                                        {getStatusBadge(transaction)}
                                        <ChevronDown aria-hidden="true" className="h-4 w-4 text-gray-500 dark:text-slate-400 transition-transform group-open:rotate-180" />
                                    </div>
                                </div>
                                <CardDescription className="text-xs">
                                    {formatDate(transaction.assigned_at)}
                                </CardDescription>
                                {/* Таймер обратного отсчета */}
                                {(transaction.status === 'assigned' || transaction.status === 'receipt_uploaded') && (
                                    <div>
                                        <CountdownTimer
                                            className="flex-wrap text-sm [&_.font-mono]:text-base"
                                            assignedAt={transaction.assigned_at}
                                            durationSeconds={transaction.duration_seconds}
                                            expiresAt={transaction.expires_at}
                                            serverTime={transaction.server_time}
                                            onExpired={() => {
                                                console.log('Время платежа истекло:', transaction.id);
                                                loadActivePayments(false);
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="text-sm space-y-1">
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-600 dark:text-slate-300">Сумма клиента:</span>
                                        <span className="font-medium text-right">{formatAmount(transaction.payer_amount, transaction.payer_currency)}</span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                        <span className="text-gray-600 dark:text-slate-300">К выдаче:</span>
                                        <span className="font-medium text-right">{formatAmount(transaction.receiver_amount, transaction.receiver_currency)}</span>
                                    </div>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-slate-400 group-open:hidden">Нажмите, чтобы раскрыть детали</span>
                                <span className="hidden text-xs text-gray-500 dark:text-slate-400 group-open:inline">Нажмите, чтобы свернуть детали</span>
                            </CardHeader>
                            </summary>
                            <CardContent className="border-t border-gray-200 dark:border-slate-700 p-3 sm:p-4 space-y-4 break-words [&_.justify-between]:gap-2 [&_.justify-between>span:last-child]:text-right">
                                    {transaction.confirmation_method === 'automatic' && <div className="rounded border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 text-sm">
                                        <strong>Подтверждено автоматически</strong>
                                    </div>}

                                <WithdrawReceiptDownload transactionId={transaction.id}
                                    receipts={getSavedReceipts(transaction)} />
                                {/* Информация о платеже */}
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="space-y-2">
                                        <h4 className="font-medium text-gray-900 dark:text-slate-100">💰 Детали платежа</h4>
                                        <div className="text-sm space-y-1">
                                            {/* Курс обмена на момент создания */}
                                            {(() => {
                                                const exchangeRate = getExchangeRate(transaction);
                                                return exchangeRate && (
                                                    <div className="flex justify-between pt-1 border-t border-gray-200 dark:border-slate-700">
                                                        <span className="text-gray-600 dark:text-slate-300 text-sm">💱 Курс обмена:</span>
                                                        <span className="font-medium text-blue-600 dark:text-blue-300 text-sm">
                                                            1 USDT = {formatAmount(exchangeRate)} {transaction.payer_currency === 'USDT' ? transaction.receiver_currency : transaction.payer_currency}
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                            {/* Заработок трейдера */}
                                            {transaction.trader_commission_amount && transaction.trader_commission_rate && (
                                                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-slate-700">
                                                    <span className="text-green-600 dark:text-green-300 font-medium">💰 Ваш заработок:</span>
                                                    <div className="text-right">
                                                        <div className="font-bold text-green-600 dark:text-green-300">
                                                            {formatAmountWithSeparator(transaction.trader_commission_amount, 'USDT')}
                                                        </div>
                                                        <div className="text-gray-500 dark:text-slate-400 text-xs">
                                                            Комиссия: {transaction.trader_commission_rate}%
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Информация об ордере и реквизитах трейдера */}
                                    {transaction.order_info && (
                                        <div className="space-y-2">
                                            <h4 className="font-medium text-gray-900 dark:text-slate-100">📋 Ваш ордер</h4>
                                            <div className="text-sm space-y-1">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-slate-300">Тип:</span>
                                                    <span className="capitalize">{transaction.order_info.order_type === 'deposit' ? 'Внесение' : 'Вывод'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-slate-300">Метод:</span>
                                                    <span className="font-medium">{getRequisiteTypeLabel(transaction.requisite_type)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-slate-300">Пара:</span>
                                                    <span>{transaction.order_info.from_currency} → {transaction.order_info.to_currency}</span>
                                                </div>

                                                {/* Реквизиты трейдера для пополнения */}
                                                {transaction.order_info.order_type === 'deposit' && (
                                                    <>
                                                        {transaction.order_info.recipient_bank && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-slate-300">Банк:</span>
                                                                <span className="font-medium">{bankDisplayName(transaction.order_info.recipient_bank)}</span>
                                                            </div>
                                                        )}
                                                        {transaction.order_info.recipient_name && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-slate-300">Получатель:</span>
                                                                <span className="font-medium">{transaction.order_info.recipient_name}</span>
                                                            </div>
                                                        )}
                                                        {transaction.order_info.phone_number && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-slate-300">Телефон:</span>
                                                                <span className="font-medium">{transaction.order_info.phone_number}</span>
                                                            </div>
                                                        )}
                                                        {transaction.order_info.recipient_card && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600 dark:text-slate-300">Карта:</span>
                                                                <span className="font-medium font-mono text-xs">{formatCardNumber(transaction.order_info.recipient_card)}</span>
                                                            </div>
                                                        )}
                                                        {transaction.order_info.notes && (
                                                            <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
                                                                <span className="text-gray-600 dark:text-slate-300">Примечание:</span>
                                                                <p className="text-gray-800 dark:text-slate-100 mt-1 text-xs bg-gray-50 dark:bg-slate-900 p-2 rounded">
                                                                    {transaction.order_info.notes}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Реквизиты пользователя для вывода */}
                                {transaction.order_info?.order_type === 'withdraw' && transaction.user_requisites && (
                                    <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                                        <h4 className="font-medium text-green-900 dark:text-green-200 mb-3">💳 Реквизиты клиента</h4>
                                        <div className="space-y-2 text-sm">
                                            {transaction.user_requisites.phone_number && (
                                                <div className="flex justify-between">
                                                    <span className="text-green-700 dark:text-green-300">Телефон:</span>
                                                    <CopyRequisite value={transaction.user_requisites.phone_number} label="номер телефона" phone>
                                                        <span className="font-medium text-green-900 dark:text-green-200">{transaction.user_requisites.phone_number}</span>
                                                    </CopyRequisite>
                                                </div>
                                            )}
                                            {transaction.user_requisites.recipient_bank && (
                                                <div className="flex justify-between">
                                                    <span className="text-green-700 dark:text-green-300">Банк:</span>
                                                    <span className="font-medium text-green-900 dark:text-green-200">
                                                        {bankDisplayName(transaction.user_requisites.recipient_bank)}
                                                    </span>
                                                </div>
                                            )}
                                            {transaction.user_requisites.recipient_name && (
                                                <div className="flex justify-between">
                                                    <span className="text-green-700 dark:text-green-300">Получатель:</span>
                                                    <span className="font-medium text-green-900 dark:text-green-200">
                                                        {transaction.user_requisites.recipient_name}
                                                    </span>
                                                </div>
                                            )}
                                            {transaction.user_requisites.recipient_card && (
                                                <div className="flex justify-between">
                                                    <span className="text-green-700 dark:text-green-300">Номер карты:</span>
                                                    <CopyRequisite value={transaction.user_requisites.recipient_card} label="номер карты">
                                                        <span className="font-medium text-green-900 dark:text-green-200 font-mono">
                                                            {formatCardNumber(transaction.user_requisites.recipient_card)}
                                                        </span>
                                                    </CopyRequisite>
                                                </div>
                                            )}
                                            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-green-900 dark:text-green-200">
                                                <span>Сумма к выдаче:</span>
                                                <span className="font-semibold text-lg">{formatAmount(transaction.receiver_amount, transaction.receiver_currency)}</span>
                                            </div>
                                        </div>
                                        {transaction.status === 'assigned' && (
                                            <div className="mt-4 pt-4 border-t border-green-300 dark:border-green-800">
                                                <div className="mb-3 space-y-2">
                                                    <p className="text-sm">Чеки: {getSavedReceipts(transaction).length + getPendingReceipts(transaction).length} из 10</p>
                                                    <label className="block text-sm" htmlFor={`withdraw-receipt-${transaction.id}`}>
                                                        Прикрепите чеки выплаты: PDF, JPG или PNG, до 10 МБ каждый
                                                    </label>
                                                    <input id={`withdraw-receipt-${transaction.id}`} type="file" multiple
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        disabled={confirmingTransaction !== null || getSavedReceipts(transaction).length + getPendingReceipts(transaction).length >= 10}
                                                        onChange={event => {
                                                            const files = Array.from(event.target.files || []);
                                                            event.target.value = '';
                                                            if (!files.length) return;
                                                            if (getSavedReceipts(transaction).length + getPendingReceipts(transaction).length + files.length > 10) {
                                                                setError('К выплате можно прикрепить не более 10 чеков'); return;
                                                            }
                                                            if (files.some(file => !file.size || file.size > 10 * 1024 * 1024 || !/\.(pdf|jpe?g|png)$/i.test(file.name))) {
                                                                setError('Выберите непустые PDF, JPG или PNG размером до 10 МБ каждый'); return;
                                                            }
                                                            const added = files.map(file => ({ id: crypto.randomUUID(), file }));
                                                            setReceiptFiles(previous => ({ ...previous, [transaction.id]: [...(previous[transaction.id] || []), ...added] }));
                                                            setError(null);
                                                        }} />
                                                    {getPendingReceipts(transaction).map(item => <div key={item.id} className="flex items-center gap-2">
                                                        <span className="text-sm break-all">{item.file.name}</span>
                                                        <Button variant="ghost" size="sm" disabled={confirmingTransaction !== null}
                                                            aria-label={`Убрать ${item.file.name}`}
                                                            onClick={() => setReceiptFiles(previous => ({ ...previous,
                                                                [transaction.id]: (previous[transaction.id] || []).filter(file => file.id !== item.id),
                                                            }))}>Убрать</Button>
                                                    </div>)}
                                                    {uploadProgress?.transactionId === transaction.id && uploadProgress.total > 0 &&
                                                        <p role="status" className="text-sm">Загружено чеков: {uploadProgress.done} из {uploadProgress.total}</p>}
                                                </div>
                                                <div className="flex gap-2">
                                                    <TimedConfirmButton
                                                        size="sm"
                                                        onClick={() => handleConfirmPayment(transaction.id)}
                                                        busy={confirmingTransaction === transaction.id} transaction={transaction}
                                                        blocked={withdrawLimit?.is_limited || confirmingTransaction !== null ||
                                                            !(getSavedReceipts(transaction).length + getPendingReceipts(transaction).length)}
                                                        className="bg-green-600 hover:bg-green-700 flex-1"
                                                    >
                                                        {confirmingTransaction === transaction.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                        ) : (
                                                            <CheckCircle className="h-4 w-4 mr-1" />
                                                        )}
                                                        Я перевел средства
                                                    </TimedConfirmButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Информация о чеке */}
                                {transaction.status === 'receipt_uploaded' && (
                                    <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                {transaction.receipt_filename ? (
                                                    <>
                                                        <Upload className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                                        <div>
                                                            <h4 className="font-medium text-blue-900 dark:text-blue-200">Чек загружен</h4>
                                                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                                                {transaction.receipt_filename} • {formatDate(transaction.receipt_uploaded_at!)}
                                                            </p>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                                        <div>
                                                            <h4 className="font-medium text-blue-900 dark:text-blue-200">Клиент подтвердил перевод</h4>
                                                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                                                Ожидает вашего подтверждения
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                {transaction.receipt_filename && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => window.open(paymentsAPI.getReceipt(transaction.id), '_blank')}
                                                    >
                                                        <ExternalLink className="h-4 w-4 mr-1" />
                                                        Открыть
                                                    </Button>
                                                )}
                                                <TimedConfirmButton
                                                    size="sm"
                                                    onClick={() => handleConfirmPayment(transaction.id)}
                                                    busy={confirmingTransaction === transaction.id} transaction={transaction}
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    {confirmingTransaction === transaction.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                    ) : (
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                    )}
                                                    Подтвердить
                                                </TimedConfirmButton>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Информация о подтверждении */}
                                {transaction.status === 'confirmed' && (
                                    <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-300" />
                                            <div>
                                                <h4 className="font-medium text-green-900 dark:text-green-200">Платеж подтвержден</h4>
                                                <p className="text-sm text-green-700 dark:text-green-300">
                                                    Подтверждено {formatDate(transaction.confirmed_at!)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Статус ожидания */}
                                {transaction.status === 'assigned' && (
                                    <>
                                        <div className="p-4 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-md">
                                            <div className="flex items-center gap-2">
                                                <Clock className="h-5 w-5 text-gray-600 dark:text-slate-300" />
                                                <div>
                                                    {transaction.order_info?.order_type === 'withdraw' ? (
                                                        <>
                                                            <h4 className="font-medium text-gray-900 dark:text-slate-100">Переведите средства клиенту</h4>
                                                            <p className="text-sm text-gray-700 dark:text-slate-200">
                                                                Переведите {formatAmount(transaction.receiver_amount, transaction.receiver_currency)} клиенту по указанным реквизитам
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h4 className="font-medium text-gray-900 dark:text-slate-100">Ожидается чек</h4>
                                                            <p className="text-sm text-gray-700 dark:text-slate-200">
                                                                Клиент должен загрузить чек об оплате
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {transaction.order_info?.order_type === 'deposit' && (
                                            <div className="flex justify-end">
                                                <TimedConfirmButton
                                                    size="sm"
                                                    onClick={() => handleConfirmPayment(transaction.id)}
                                                    busy={confirmingTransaction === transaction.id} transaction={transaction}
                                                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                                                >
                                                    {confirmingTransaction === transaction.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                    ) : (
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                    )}
                                                    Подтвердить
                                                </TimedConfirmButton>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                            </details>
                        </Card>
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-300" />
                <span className="ml-2 text-gray-600 dark:text-slate-300">Загружаем активные платежи...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Активные платежи — {direction === 'deposit' ? 'Пополнение' : 'Вывод'}</h2>
                    <p className="text-gray-600 dark:text-slate-300 mt-2">Платежи по вашим ордерам, требующие внимания</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadActivePayments(true)}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <RefreshCw className="h-4 w-4" />
                    )}
                </Button>
            </div>

            {confirmationNotice && (
                <div role="status" className="p-4 text-green-800 dark:text-green-200 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                    {confirmationNotice}
                </div>
            )}

            {error && (
                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                    {formatTraderMessage(error, offsetMinutes)}
                </div>
            )}

            {direction === 'withdraw' && withdrawLimit?.is_limited && (
                <div className="p-4 text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-800 rounded-md">
                    <p className="font-medium">Лимит подтверждений выплат: {withdrawLimit.completed_count_2h} из {withdrawLimit.limit}</p>
                    <p>Новые выплаты не назначаются, подтверждение следующих выплат временно недоступно.</p>
                    {withdrawLimit.next_available_at && (
                        <p>Доступно после: {formatDate(withdrawLimit.next_available_at)}</p>
                    )}
                </div>
            )}

            {transactions.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Clock className="h-16 w-16 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">{direction === 'deposit' ? 'Нет активных пополнений' : 'Нет активных выводов'}</h3>
                        <p className="text-gray-500 dark:text-slate-400 text-center">
                            Когда кто-то выберет ваш ордер для оплаты, он появится здесь
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-6">
                    <div className="space-y-4">
                        {transactions.map(renderPayment)}
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalItems={totalItems}
                        pageSize={pageSize}
                    />
                </div>
            )}
        </div>
    );
};
